import { useState } from "react";
import { Link } from "react-router-dom";
import { X, LogOut } from "lucide-react";
import useHandleLogout from "src/hooks/useHandleLogout";
import { selectUserState } from "src/lib/features/auth/authSlice";
import { useTypedSelector } from "src/lib/store/store";
import ReferralModal from "../Modal/ReferralModal";
import MenuItems from "./MenuItems";
import { SocialLinks } from "./SideNav";
import { CompanyAvatar } from "./UserInfo";
import { NAV_GROUPS } from "./navItems";

// See the note in SideNav.jsx: served from `public/`, not imported.
const LogoLockup = `${process.env.PUBLIC_URL}/images/droomlogo.svg`;

/**
 * Mobile drawer. The desktop rail is hidden below lg, so this carries the full
 * navigation in the same navy register rather than a second visual language.
 */
const MobileMenu = ({ showMobileMenu, setShowMobileMenu }) => {
  const [showReferralModal, setShowReferralModal] = useState(false);
  const { handleLogout } = useHandleLogout();
  const user = useTypedSelector(selectUserState).user;

  const close = () => setShowMobileMenu && setShowMobileMenu(false);

  if (!showMobileMenu) return null;

  const companyName = user?.userCompany?.name ?? "";

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Close menu"
        onClick={close}
        className="absolute inset-0 h-full w-full bg-brand-navy/50"
      />

      <div className="relative flex h-full w-full max-w-xs flex-col bg-brand-navy text-white shadow-ui-lg">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-4">
          {/* Same lockup as the rail, so the drawer and the desktop shell
              show the same brand rather than artwork in one and type in the
              other. Not a link here, so the image carries the name. */}
          <img
            src={LogoLockup}
            alt="Droomwork"
            width={255}
            height={70}
            className="h-8 w-auto"
          />
          <button
            type="button"
            onClick={close}
            aria-label="Close menu"
            className="-mr-2 rounded-full p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-2 py-4">
          <Link
            to="/profile"
            onClick={close}
            className="mx-1 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3"
          >
            <CompanyAvatar
              photo={user?.photo}
              name={companyName}
              className="h-10 w-10"
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white">
                {companyName || "My profile"}
              </span>
              <span className="block text-[10px] uppercase tracking-wider text-white/50">
                Hiring Client
              </span>
            </span>
          </Link>

          {NAV_GROUPS.map((group, index) => (
            <MenuItems
              key={group.label}
              label={group.label}
              items={group.items}
              badge={group.badge}
              onNavigate={close}
              className={index > 0 ? "border-t border-white/10 pt-4" : undefined}
            />
          ))}
        </div>

        <div className="shrink-0 space-y-3 border-t border-white/10 p-2 pb-6">
          <button
            type="button"
            onClick={() => handleLogout()}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            Sign out
          </button>

          <div className="px-3">
            <div className="pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">
              Follow us
            </div>
            <SocialLinks />
          </div>
        </div>
      </div>

      <ReferralModal
        open={showReferralModal}
        closeModal={() => setShowReferralModal(false)}
      />
    </div>
  );
};

export default MobileMenu;
